import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BlankState,
  Box,
  Button,
  Grid,
  Icon,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  PillVariant,
  TableState,
  Tooltip,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import IconButton from '@mui/material/IconButton';
import {
  AccessArrangementRequestSelection,
  SubjectPaper,
} from '../components/candidate/AccessArrangementRequest/AccessArrangementRequestModels';
import { stringToLongDateAndShortTime, stringToSemanticDisplay } from '../../../utils/DateUtils';
import {
  AccessArrangementResultListing,
  ApplicationStatus,
} from '../components/candidate/AccessArrangements/AccessArrangementsModels';
import RouteKey from '../../../routes/key';

const useAccessArrangementRequestStyles = makeStyles()((theme) =>
({
  spaceBottom: {
    marginBottom: theme.spacing(2),
  },
  examLevel: {
    color: theme.color.text.textHeader,
    fontWeight: 800,
  },
  buttonContainer: {
    display: 'inline-flex',
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  buttonRemove: {
    color: theme.color.state.stateError,
  },
  button: {
    margin: 'auto',
    paddingTop: 0,
    padding: 10,
    minWidth: 0,
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
  },
  dropped: {
    marginLeft: theme.spacing(1),
    color: theme.color.state.stateError,
  },
  emptyReserve: {
    height: 314,
  },
  emptyContainer: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 77,
    paddingBottom: 77,
  },
}));

const useAccessArrangementsStyles = makeStyles()((theme) =>
({
  buttonContainer: {
    display: 'inline-flex',
  },
  iconButtonContainer: {
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  buttonRemove: {
    paddingLeft: '10px !important',
    color: theme.color.state.stateError,
  },
  icon: {
    margin: 'auto',
  },
  iconRemove: {
    margin: 'auto',
  },
  button: {
    margin: 'auto',
    padding: 0,
    minWidth: 0,
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
  },
  paddingRight6: {
    paddingRight: theme.spacing(6),
  },
}));

export const useAccessArrangementRequest = (setButtonDisable: (buttonDisable: boolean) => void) => {
  const checkSubjectPaperChange = useCallback(
    (currentSubjectPapers: SubjectPaper[], selectedSubjectPapers?: SubjectPaper[]) => {
      if (!currentSubjectPapers || currentSubjectPapers.length === 0) {
        setButtonDisable(true);
        return;
      }

      if (selectedSubjectPapers) {
        if (currentSubjectPapers.length !== selectedSubjectPapers.length) {
          setButtonDisable(false);
          return;
        }

        const current = [...currentSubjectPapers].sort(compare);
        const selected = [...selectedSubjectPapers].sort(compare);

        for (let i = 0; i < currentSubjectPapers.length; i += 1) {
          if (
            current[i].examRegistrationDetailUuid !== selected[i].examRegistrationDetailUuid ||
            current[i].paperUuid !== selected[i].paperUuid
          ) {
            setButtonDisable(false);
            return;
          }
        }
        setButtonDisable(true);
        return;
      }

      setButtonDisable(false);
    },
    [setButtonDisable]
  );

  const compare = (subjectPaper1: any, subjectPaper2: any) => {
    const compare1 = subjectPaper1.examRegistrationDetailUuid.localeCompare(
      subjectPaper2.examRegistrationDetailUuid
    );

    if (compare1 !== 0) {
      return compare1;
    }

    return subjectPaper1.paperUuid.localeCompare(subjectPaper2.paperUuid);
  };

  return checkSubjectPaperChange;
};

export const useAccessArrangementRequestColumns = (
  state: TableState,
  handleOpenModal: (addMode: boolean, dataIndex: number) => void,
  handleOpenDeleteModal: (detailUuid: string) => void,
  accessArrangementRequestSelections?: AccessArrangementRequestSelection
): MUIDataTableColumnDef[] => {
  const { classes } = useAccessArrangementRequestStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const hasMoa = (dataIndex: number) =>
    accessArrangementRequestSelections &&
    Object.keys(accessArrangementRequestSelections).some(
      (moa) => moa === state.data[dataIndex].modeOfAssessment.iexamsCode
    );

  return [
    {
      name: 'requestType',
      label: 'Request Type',
      options: {
        setCellProps: () => ({
          style: { width: '20%' },
        }),
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined) {
            return [];
          }

          return state.data[dataIndex].accessArrangementType.description;
        },
      },
    },
    {
      name: 'remarks',
      label: 'Remarks',
      options: {
        sort: false,
        setCellProps: () => ({
          style: {
            style: { width: '25%' },
            wordWrap: 'break-word',
          },
        }),
        customBodyRenderLite: (dataIndex: number) => {
          if (!state.data[dataIndex].remarks) {
            return '-';
          }

          return state.data[dataIndex].remarks;
        },
      },
    },
    {
      name: 'modeOfAssessment',
      label: 'Mode of Assessment',
      options: {
        setCellProps: () => ({
          style: { width: '13%' },
        }),
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined) {
            return [];
          }

          return state.data[dataIndex].modeOfAssessment.description;
        },
      },
    },
    {
      name: 'examSeries',
      label: 'Exam Series',
      options: {
        sort: false,
        setCellProps: () => ({
          style: { width: '12%' },
        }),
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined) {
            return [];
          }

          const columnData: any[] = Object.keys(state.data[dataIndex].subjectPapers);

          const cell: any[] = [];
          const heights: number[] = [];

          const examLevelSubjectPapers: any[] = Object.values(state.data[dataIndex].subjectPapers);

          examLevelSubjectPapers.forEach((examLevelSubjectPaper) => {
            const examLevels: string[] = Object.keys(examLevelSubjectPaper);
            const subjectPapers: any[] = Object.values(examLevelSubjectPaper);
            let height = 0;

            examLevels.forEach((examLevel, index) => {
              height += 40;

              subjectPapers[index].forEach(() => {
                height += 24;
              });
            });

            heights.push(height);
          });

          columnData.forEach((examSeries, index) => {
            cell.push(
              <Box key={examSeries} style={{ minHeight: heights[index] }}>
                {examSeries}
              </Box>
            );
          });

          return cell;
        },
      },
    },
    {
      name: 'subjectPaper',
      label: 'Subject(s)/Paper(s)',
      options: {
        sort: false,
        setCellProps: () => ({
          style: { width: '15%' },
        }),
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined) {
            return [];
          }

          const cell: any[] = [];

          const columnData: any[] = Object.values(state.data[dataIndex].subjectPapers);

          columnData.forEach((examLevelSubjectPaper) => {
            const examLevels: string[] = Object.keys(examLevelSubjectPaper);
            const subjectPapers: any[] = Object.values(examLevelSubjectPaper);

            cell.push(
              examLevels.map((examLevel, index) => (
                <Box key={examLevel} className={classes.spaceBottom}>
                  <Typography variant="h6" className={classes.examLevel}>
                    {examLevel}
                  </Typography>
                  {subjectPapers[index].map((subjectPaper: SubjectPaper) => (
                    <Box
                      key={`${examLevel}-${subjectPaper.subject}`}
                      display="flex"
                      whiteSpace="nowrap"
                    >
                      {subjectPaper.subject}
                      {subjectPaper.dropped && (
                        <Typography variant="body2" className={classes.dropped}>
                          (Dropped)
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ))
            );
          });

          return cell;
        },
      },
    },
    {
      name: 'action',
      label: 'Action',
      options: {
        sort: false,
        setCellProps: () => ({
          style: { width: '15%' },
        }),
        customBodyRenderLite: (dataIndex: number) => (
          <Grid container spacing={2}>
            {hasMoa(dataIndex) && (
              <Grid className={classes.buttonContainer}>
                <Button
                  id={`button-accessArrangement-edit-${dataIndex}`}
                  className={`${classes.button} ${classes.buttonFont}`}
                  onClick={() => handleOpenModal(false, dataIndex)}
                  variant="text"
                  color="primary"
                  size="small"
                  startIcon={
                    <Icon size="M" name={IconName.edit} color={theme.color.icon.iconSelected} />
                  }
                >
                  {t('accessArrangement->>AccessArrangementRequest.Button.Edit')}
                </Button>
              </Grid>
            )}
            {state.data[dataIndex].isDraft && (
              <Grid className={classes.buttonContainer}>
                <Button
                  id={`button-accessArrangement-delete-${dataIndex}`}
                  className={`${classes.button} ${classes.buttonFont} ${classes.buttonRemove}`}
                  onClick={() => handleOpenDeleteModal(state.data[dataIndex])}
                  variant="text"
                  size="small"
                  startIcon={
                    <Icon size="M" name={IconName.trash} color={theme.color.icon.iconTrash} />
                  }
                >
                  {t('accessArrangement->>AccessArrangementRequest.Button.Remove')}
                </Button>
              </Grid>
            )}
          </Grid>
        ),
      },
    },
  ];
};

export const useAccessArrangementRequestOptions = (): MUIDataTableOptions => {
  const { classes } = useAccessArrangementRequestStyles();
  const { t } = useTranslation();

  return {
    textLabels: {
      body: {
        noMatch: (
          <>
            <Box className={classes.emptyReserve}>
              <></>
            </Box>
            <Box className={classes.emptyContainer}>
              <BlankState label={t('accessArrangement->>AccessArrangementRequest.Empty')} />
            </Box>
          </>
        ),
      },
    },
    sort: true,
    setRowProps: () => ({ style: { verticalAlign: 'top' } }),
  };
};

export const useAccessArrangementsColumns = (
  handleOpenDeleteModal: (uuid: string) => void,
  accessArrangementRequest?: AccessArrangementResultListing
): MUIDataTableColumnDef[] => {
  const { classes } = useAccessArrangementsStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();

  const { accessArrangement } = RouteKey.child;
  const { importantNotes, root, summary } = RouteKey['access-arrangement'].child;

  const handleView = (uuid: string) => {
    history.push(`/${accessArrangement}/${root.route}/${summary.route}/${uuid}`);
  };

  const handleApply = (uuid: string) =>
    history.push(`/${accessArrangement}/${root.route}/${importantNotes.route}`);

  return [
    {
      name: 'applicationId',
      label: 'Application ID',
      options: {
        setCellProps: () => ({ style: { width: '20%', paddingTop: '30px' } }),
      },
    },
    {
      name: 'examLevels',
      label: 'Exam Level(s)',
      options: {
        setCellProps: () => ({ style: { width: '20%', paddingTop: '30px' } }),
      },
    },
    {
      name: 'applicationDate',
      label: 'Application Date/Time',
      options: {
        setCellProps: () => ({ style: { width: '20%', paddingTop: '30px' } }),
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined || accessArrangementRequest === undefined) {
            return [];
          }

          const accessArrangementResultModel =
            accessArrangementRequest.accessArrangementResultModels[dataIndex];

          return stringToLongDateAndShortTime(
            accessArrangementResultModel.requestStatus === ApplicationStatus.SUBMITTED
              ? accessArrangementResultModel.amendmentDateTime
              : accessArrangementResultModel.applicationDateTime
          );
        },
      },
    },
    {
      name: 'aaOutcomeLastUpdateDate',
      label: 'Application Outcome',
      options: {
        setCellProps: () => ({ style: { width: '20%', paddingTop: '30px' } }),
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined || accessArrangementRequest === undefined) {
            return '-';
          }

          const accessArrangementResultModel =
            accessArrangementRequest.accessArrangementResultModels[dataIndex];

          if (!accessArrangementResultModel.aaOutcomeLastUpdateDate) {
            return '-';
          }

          return `${t(
            'accessArrangement->>AccessArrangements.LastUpdatedOn'
          )} ${stringToSemanticDisplay(
            accessArrangementResultModel.aaOutcomeLastUpdateDate?.toString()
          )}`;
        },
      },
    },
    {
      name: 'requestStatus',
      label: 'Status',
      options: {
        setCellProps: () => ({ style: { width: '18%', paddingTop: '30px' } }),
        customBodyRender: (value: ApplicationStatus) => {
          let variant: PillVariant;

          switch (value) {
            case ApplicationStatus.COMPLETED:
              variant = 'positive';
              break;

            case ApplicationStatus.SUBMITTED:
            case ApplicationStatus.PROCESSING:
            case ApplicationStatus.DRAFT:
              variant = 'info';
              break;

            case ApplicationStatus.WITHDRAWN:
              variant = 'default';
              break;

            default:
              return null;
          }

          return <Pill variant={variant} label={value} />;
        },
      },
    },
    {
      name: 'action',
      label: 'Action(s)',
      options: {
        setCellProps: () => ({ style: { width: '22%' } }),
        customBodyRenderLite: (dataIndex: number) => (

          <Box className={classes.buttonContainer}>
            {accessArrangementRequest?.accessArrangementResultModels[dataIndex].requestStatus ===
              ApplicationStatus.SUBMITTED && (
                <>
                  <Button
                    id={`button-accessArrangement-view-${dataIndex}`}
                    className={`${classes.button} ${classes.buttonFont}`}
                    onClick={() =>
                      handleView(
                        accessArrangementRequest?.accessArrangementResultModels[dataIndex]?.uuid
                      )
                    }
                    variant="text"
                    color="primary"
                    size="small"
                  >
                    {t('accessArrangement->>AccessArrangements.View')}
                  </Button>
                  {accessArrangementRequest?.accessArrangementResultModels[dataIndex]
                    .amendmentDraft && (
                      <Tooltip
                        arrow
                        placement="bottom"
                        title={<>{t('accessArrangement->>AccessArrangements.AmendmentDraft')}</>}
                      >
                        <IconButton>
                          <Icon name={IconName.alertOff} color={theme.color.icon.iconDefault} />
                        </IconButton>
                      </Tooltip>
                    )}
                </>
              )}
            {accessArrangementRequest?.accessArrangementResultModels[dataIndex].requestStatus ===
              ApplicationStatus.DRAFT && (
                <Button
                  id={`button-accessArrangement-continue-${dataIndex}`}
                  className={`${classes.button} ${classes.paddingRight6} ${classes.buttonFont}`}
                  onClick={() =>
                    handleApply(
                      accessArrangementRequest?.accessArrangementResultModels[dataIndex]?.uuid
                    )
                  }
                  variant="text"
                  color="primary"
                  size="small"
                  disabled={!accessArrangementRequest.accessArrangementOpen}
                >
                  {t('accessArrangement->>AccessArrangements.Continue')}
                </Button>
              )}
            {accessArrangementRequest?.accessArrangementResultModels[dataIndex].requestStatus ===
              ApplicationStatus.DRAFT && (
                <Box className={`${classes.buttonContainer} ${classes.iconButtonContainer}`}>
                  <Icon
                    size="M"
                    className={classes.iconRemove}
                    name={IconName.trash}
                    color={theme.color.icon.iconTrash}
                  />
                  <Button
                    id="button-accessArrangement-delete"
                    className={`${classes.button} ${classes.buttonFont} ${classes.buttonRemove}`}
                    onClick={() =>
                      handleOpenDeleteModal(
                        accessArrangementRequest.accessArrangementResultModels[dataIndex].uuid
                      )
                    }
                    variant="text"
                    size="small"
                  >
                    {t('accessArrangement->>AccessArrangements.Delete')}
                  </Button>
                </Box>
              )}
          </Box>
        ),
      },
    },
  ];
};
